import { useEffect, useState } from "react";
import { CheckCircle2, Copy, CreditCard, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, get } from "@/lib/api";
import { Button } from "@/components/ui/button";

const Row = ({ ok, label, testId }) => (
  <li className="flex items-center gap-2 text-sm" data-testid={testId}>
    {ok ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" /> : <XCircle className="h-4 w-4 shrink-0 text-rose-600" />}
    <span className={ok ? "text-slate-800" : "text-rose-700"}>{label}</span>
  </li>
);

export const SquareAdmin = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    get("/platform/square-status").then(setS).catch((e) => toast.error(errorMessage(e)));
  }, []);
  if (!s) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const copy = () => navigator.clipboard.writeText(s.webhook_url).then(() => toast.success("Webhook address copied"));
  return (
    <section className="max-w-3xl space-y-5 rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="square-admin">
      <div className="flex gap-3">
        <CreditCard className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">Square payments</h2>
          <p className="mt-1 text-sm text-slate-600">New monthly plans and text bundles are paid on Square&apos;s secure checkout in £. Companies already paying through Stripe stay on Stripe until they change plan.</p>
        </div>
      </div>
      <ul className="space-y-2">
        <Row ok={s.configured} label={s.configured ? "Access token and location saved" : "Access token or location ID missing"} testId="square-status-keys" />
        <Row ok={s.connected} label={s.connected ? `Connected to ${s.location_name || "your Square location"} (${s.environment}${s.currency ? `, ${s.currency}` : ""})` : `Not connected${s.error ? `: ${s.error}` : ""}`} testId="square-status-connected" />
        <Row ok={s.webhook_key_set} label={s.webhook_key_set ? "Webhook signature key saved" : "Webhook signature key missing, so payments can't be confirmed yet"} testId="square-status-webhook-key" />
      </ul>
      <div className="space-y-2 rounded-lg bg-slate-50 p-4">
        <p className="text-sm font-semibold text-slate-900">Webhook address for your Square Developer dashboard</p>
        <div className="flex flex-wrap items-center gap-2">
          <code className="break-all rounded bg-white px-2 py-1 text-xs text-slate-800" data-testid="square-webhook-url">{s.webhook_url}</code>
          <Button size="sm" variant="outline" onClick={copy} data-testid="square-webhook-copy"><Copy className="mr-1 h-4 w-4" /> Copy</Button>
        </div>
        <p className="text-xs text-slate-600">In Square: Developer dashboard → your app → Webhooks → Add subscription ({s.environment === "sandbox" ? "Sandbox" : "Production"}). Paste this address, tick these events, then save the signature key in the app&apos;s secure settings:</p>
        <p className="font-data text-xs text-slate-700" data-testid="square-webhook-events">{s.events.join(", ")}</p>
        {s.environment === "sandbox" && <p className="text-xs font-semibold text-amber-700" data-testid="square-sandbox-note">Test mode (Square Sandbox): no real money moves. Pay with Square's test card 4111 1111 1111 1111, any future expiry, any CVV.</p>}
        <p className="text-xs text-slate-500">Use the address of your live site (warrant-whizz.emergent.host) for real payments, not the preview address.</p>
      </div>
    </section>
  );
};
